<template>
  <div class="columns">
    <div class="column is-two-thirds">
      <h2 class="title is-2">
        Edit profile
      </h2>
      <br>
      <form
        class="form"
        @submit.prevent="submit"
      >
        <h3 class="title is-3">
          Details
        </h3>
        <div
          v-if="success"
          class="message is-success has-text-left"
        >
          <div class="message-body">
            Profile updated.
          </div>
        </div>
        <div
          v-for="(err, i) in formErrors.non_field_errors"
          :key="i"
          class="message is-danger has-text-left"
        >
          <div class="message-body">
            {{ err }}
          </div>
        </div>
        <div class="field">
          <label class="label">Company name</label>
          <div class="control">
            <input
              id="id_team"
              v-model.trim="profileData.team.name"
              :class="errorClass('team.name')"
              :disabled="busy"
              required
              type="text"
              name="team"
              autofocus
              class="input"
            >
          </div>
          <p
            v-for="(err, i) in getErrors('team.name')"
            :key="i"
            class="help is-danger has-text-left"
          >
            {{ err|capitalize }}
          </p>
        </div>
        <div class="field">
          <label class="label">Your name</label>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <input
                  id="id_first_name"
                  v-model.trim="profileData.user.first_name"
                  :class="errorClass('user.first_name')"
                  :disabled="busy"
                  required
                  type="text"
                  name="first_name"
                  class="input"
                  placeholder="First"
                >
              </div>
            </div>
            <div class="field">
              <div class="control">
                <input
                  id="id_last_name"
                  v-model.trim="profileData.user.last_name"
                  :class="errorClass('user.last_name')"
                  :disabled="busy"
                  required
                  type="text"
                  name="last_name"
                  class="input"
                  placeholder="Last"
                >
              </div>
            </div>
          </div>
          <!-- /.field-body -->
          <p
            v-for="(err, i) in getErrors('user.first_name')"
            :key="i"
            class="help is-danger has-text-left"
          >
            {{ err|capitalize }}
          </p>
          <p
            v-for="(err, i) in getErrors('user.last_name')"
            :key="i"
            class="help is-danger has-text-left"
          >
            {{ err|capitalize }}
          </p>
        </div>
        <div class="field">
          <label class="label">Email address</label>
          <div class="control">
            <input
              id="id_email"
              v-model.trim="profileData.user.email"
              :class="errorClass('user.email')"
              :disabled="busy"
              required
              type="email"
              name="email"
              class="input"
            >
          </div>
          <p
            v-for="(err, i) in getErrors('user.email')"
            :key="i"
            class="help is-danger has-text-left"
          >
            {{ err|capitalize }}
          </p>
          <p class="help">
            You will need to log in again if you change your email address.
          </p>
        </div>
        <br>
        <h3 class="title is-3">
          Update password
        </h3>
        <div class="field">
          <div class="field-body">
            <div class="field">
              <div class="control">
                <label class="label">Current password</label>
                <input
                  id="id_old_password"
                  v-model.trim="profileData.user.old_password"
                  :class="errorClass('user.old_password')"
                  :disabled="busy"
                  type="password"
                  name="old_password"
                  class="input"
                >
              </div>
              <p
                v-for="(err, i) in getErrors('user.old_password')"
                :key="i"
                class="help is-danger has-text-left"
              >
                {{ err|capitalize }}
              </p>
            </div>
            <div class="field">
              <div class="control">
                <label class="label">New password</label>
                <input
                  id="id_password"
                  v-model.trim="profileData.user.password"
                  :class="errorClass('user.password')"
                  :disabled="busy"
                  type="password"
                  name="password"
                  class="input"
                >
              </div>
              <p
                v-for="(err, i) in getErrors('user.password')"
                :key="i"
                class="help is-danger has-text-left"
              >
                {{ err|capitalize }}
              </p>
            </div>
          </div>
          <!-- /.field-body -->
        </div>
        <br>
        <button
          type="submit"
          class="button is-block is-primary is-large"
          :class="{ 'is-loading': busy }"
          :disabled="busy"
        >
          Save
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import teamClient from '@/api/teams.js'

export default {
  name: 'ProfileUpdate',

  data () {
    return {
      formErrors: {},
      success: false,
      busy: false,
      originalEmail: '',
      profileData: {
        user: {
          email: '',
          first_name: '',
          last_name: '',
          old_password: '',
          password: ''
        },
        team: {
          pk: '',
          name: ''
        }
      }
    }
  },

  computed: {
    fullName: function () {
      return this.$store.getters['user/fullName']
    }
  },

  created () {
    this.$store.dispatch('user/setMe').then(() => {
      this.getProfileData()
    })
  },

  methods: {
    getProfileData () {
      const user = Object.assign({}, this.$store.getters['user/currentUser'])
      const team = Object.assign({}, this.$store.getters['user/currentTeam'])

      const userKeys = Object.keys(this.profileData.user)
      const teamKeys = Object.keys(this.profileData.team)

      for (const [key, value] of Object.entries(user)) {
        if (userKeys.includes(key)) {
          this.profileData.user[key] = value
        }
      }

      for (const [key, value] of Object.entries(team)) {
        if (teamKeys.includes(key)) {
          this.profileData.team[key] = value
        }
      }

      this.originalEmail = this.profileData.user.email
    },

    submit () {
      this.formErrors = {}
      this.success = false
      this.busy = true

      teamClient
        .partialUpdate(this.profileData)
        .then(response => {
          const responseUser = response.data

          // Clear passwords, don't want this hanging around whatever we do next.
          this.profileData.user.old_password = ''
          this.profileData.user.password = ''

          if (this.originalEmail !== responseUser.email) {
            this.$store.dispatch('auth/logout')
            this.$store.dispatch('auth/showLoginModal')
          } else {
            return this.$store.dispatch('user/setMe').then(() => {
              this.success = true
              this.getProfileData()
            })
          }
        })
        .catch(error => {
          if (error.response && error.response.status === 400) {
            this.$set(this, 'formErrors', error.response.data)
            return
          }
          const errors = { non_field_errors: ['Something went wrong saving your profile. Please try again.'] }
          this.$set(this, 'formErrors', errors)
          return Promise.reject(error.response || error)
        })
        .finally(() => {
          this.busy = false
        })
    },

    getErrors (path) {
      const parts = path.split('.')
      return parts.reduce((p, c) => (p && p[c]) || null, this.formErrors)
    },

    isValid (path) {
      return this.getErrors(path) === null
    },

    errorClass (path) {
      return !this.isValid(path) ? 'is-danger' : ''
    }
  },

  metaInfo: {
    title: 'Profile settings'
  }
}
</script>

<style>
</style>
